import { telegramHref } from "@/lib/social-links";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/circles")({
  component: () => <Outlet />,
});

export type CircleRow = {
  id: string;
  name: string;
  description: string | null;
  cover: string;
  logo_url: string | null;
  host_user_id: string | null;
  host_note: string | null;
  topics: string[] | null;
  city: string | null;
  status: string;
  created_at: string;
  is_deleted?: boolean;
  has_chat: boolean;
  instagram_url: string | null;
  telegram_url: string | null;
  chat_access: string;
  is_official?: boolean;
};

export const CIRCLE_COLS =
  "id, name, description, cover, logo_url, host_user_id, host_note, topics, city, status, created_at, is_deleted, has_chat, instagram_url, telegram_url, chat_access, is_official";

export type CircleHostRow = { circle_id: string; user_id: string; created_at?: string };

/** Создатель (host_user_id) или любой со-хранитель из circle_hosts. */
export function isCircleHost(
  c: { id: string; host_user_id: string | null },
  uid: string | null | undefined,
  hosts: CircleHostRow[] = [],
): boolean {
  if (!uid) return false;
  return c.host_user_id === uid || hosts.some((h) => h.circle_id === c.id && h.user_id === uid);
}

/** Со-хранители кругов (без создателя). */
export function useCircleCohosts(circleIds: string[]) {
  const key = [...circleIds].sort().join(",");
  return useQuery({
    queryKey: ["circle-cohosts", key],
    enabled: circleIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_hosts")
        .select("circle_id, user_id, created_at")
        .in("circle_id", circleIds)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CircleHostRow[];
    },
  });
}

/** Ссылка на чат круга — RLS отдаёт её только участникам, хранителю и админу. */
export function useCircleChatLink(circleId: string, enabled = true) {
  return useQuery({
    queryKey: ["circle-chat-link", circleId],
    enabled: enabled && !!circleId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_chat_links")
        .select("url")
        .eq("circle_id", circleId)
        .maybeSingle();
      if (error) throw error;
      return data?.url ?? null;
    },
  });
}

export const CHAT_URL_HINT = "Ссылку увидят только участники сообщества после вступления";
export const CHAT_CLOSED_HINT = "Участники попросят доступ, а ты добавишь их в чат сам(а)";
export const IG_HINT = "Публичная страница сообщества. Её увидят все на странице сообщества";
export const TG_HINT = "Публичный канал. Его увидят все на странице сообщества";

export { normalizeUrl } from "@/lib/circle-manage";

export { CHAT_URL_ERROR } from "@/lib/circle-manage";

/** Значение из профиля (ссылка или ник) → URL. Ник: t.me для telegram, instagram для остальных. */
export function profileHandleToUrl(raw: string, kind: "instagram" | "telegram"): string {
  const v = raw.trim();
  if (/^https?:\/\//i.test(v) || /^mailto:/i.test(v)) return v;
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}(\/|$)/i.test(v)) return `https://${v}`;
  const nick = v.replace(/^@/, "");
  if (kind === "telegram") return telegramHref(v) ?? `https://t.me/${nick}`;
  return `https://instagram.com/${nick}`;
}

export function useCircleMembership() {
  const { user } = useAuth();
  const uid = user?.id;
  const qc = useQueryClient();

  const join = async (id: string) => {
    if (!uid) return;
    const { getCircleRef, clearCircleRef } = await import("@/lib/circle-invite");
    const ref = getCircleRef(id);
    const invitedBy = ref && ref !== uid ? ref : null;
    const { error } = await supabase
      .from("circle_members")
      .insert({ circle_id: id, user_id: uid, invited_by: invitedBy });
    if (error) {
      toast.error("Не удалось вступить в сообщество");
      return;
    }
    clearCircleRef(id);
    toast.success("Вы в сообществе");
    await qc.invalidateQueries({ queryKey: ["circle-members"] });
  };

  const leave = async (id: string) => {
    if (!uid) return;
    const { error } = await supabase
      .from("circle_members")
      .delete()
      .eq("circle_id", id)
      .eq("user_id", uid);
    if (error) {
      toast.error("Не удалось выйти из сообщества");
      return;
    }
    toast.success("Вы вышли из сообщества");
    await qc.invalidateQueries({ queryKey: ["circle-members"] });
  };

  return { uid, join, leave };
}

/* --------------------- Soft delete / restore circle ------------------------ */

/** Мягкое удаление: строка остаётся в БД, ставится флаг is_deleted. */
export function useDeleteCircle(onDeleted?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("circles")
        .update({ is_deleted: true })
        .eq("id", id)
        .select("id")
        .maybeSingle();
      if (error) throw error;
    },
    onSuccess: async () => {
      toast.success("Сообщество удалено");
      await qc.invalidateQueries({ queryKey: ["circles"] });
      await qc.invalidateQueries({ queryKey: ["circle-members"] });
      onDeleted?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** Восстановление удалённого круга — только для админа. */
export function useRestoreCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("circles").update({ is_deleted: false }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      toast.success("Сообщество восстановлено");
      await qc.invalidateQueries({ queryKey: ["circles"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}


export function DeleteCircleButton({
  circleId,
  circleName,
  className,
  label = "Удалить сообщество",
  onDeleted,
}: {
  circleId: string;
  circleName: string;
  className?: string;
  label?: string;
  onDeleted?: () => void;
}) {
  const del = useDeleteCircle(onDeleted);
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={del.isPending}
          className={
            className ??
            "h-9 rounded-xl border-destructive/40 text-sm text-destructive hover:bg-destructive/10"
          }
        >
          {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-sm rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Удалить сообщество «{circleName}»?</AlertDialogTitle>
          <AlertDialogDescription>
            Участники и привязка к мероприятиям будут скрыты. Сообщество можно будет восстановить только
            через администратора.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Отмена</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => del.mutate(circleId)}
          >
            Да, удалить сообщество
          </AlertDialogAction>

        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
